import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import api from "../../../../../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const EmergencyResponseBarChart = ({ month, year, setExportFunctions, filters }) => {
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [noData, setNoData] = useState(false);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/ems/average-emergency-response-time", {
          params: { month, year },
        });

        if (
          response.data.avg_response_times &&
          response.data.avg_response_times.length > 0
        ) {
          setChartData({
            labels: response.data.week_labels,
            data: response.data.avg_response_times,
          });
          const allZero = response.data.avg_response_times.every((v) => v === 0);
          setNoData(allZero);
        } else {
          setChartData({ labels: [], data: [] });
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching response time data:", error);
        setChartData({ labels: [], data: [] });
        setNoData(true);
      }
    };

    fetchData();
  }, [month, year]);

  const exportPDF = async () => {
  try {
    const dataURI = await ApexCharts.exec("emergency-response-bar", "dataURI", {
      scale: 2,
      width: 800,
    });

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const monthName = months[month - 1];

    // Add chart image
    const chartHeight = 100;
    const chartTopMargin = 40;
    doc.addImage(dataURI.imgURI, "PNG", 10, chartTopMargin, 190, chartHeight);

    // Define table options
    const options = {
      startY: chartTopMargin + chartHeight + 20, // Place table below chart with 20px gap
      head: [["Week", "Average Response Time (minutes)"]],
      body: chartData.labels.map((label, index) => [
        label,
        chartData.data[index]?.toFixed(2) || "0.00",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [0, 123, 255], // Blue header
        textColor: 255,
        fontStyle: "bold",
      },
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      margin: { left: 10, right: 10 },
      didDrawPage: (data) => {
        // Header - Main title
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Average Emergency Response Time Report", pageWidth / 2, 20, {
          align: "center",
        });

        // Header - Period information
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Period: ${monthName} ${year}`, pageWidth / 2, 30, {
          align: "center",
        });

        // Footer - Page number
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    };

    // Add table only if data exists
    if (chartData.labels.length > 0 && !noData) {
      autoTable(doc, options);
    } else {
      // Add message if no data
      doc.setFontSize(10);
      doc.text(
        "No data available for the selected period.",
        pageWidth / 2,
        chartTopMargin + chartHeight + 20,
        { align: "center" }
      );
    }

    doc.save(`emergency_response_time_${year}_${month}.pdf`);
  } catch (error) {
    console.error("Error exporting PDF:", error);
  }
};

  const exportXML = () => {
    let xmlData = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xmlData += `<EmergencyResponseTimes month="${month}" year="${year}">\n`;
    
    if (chartData.labels.length > 0 && !noData) {
      chartData.labels.forEach((label, index) => {
        xmlData += `  <Week>\n`;
        xmlData += `    <Label>${label}</Label>\n`;
        xmlData += `    <ResponseTime>${chartData.data[index]?.toFixed(2) || '0.00'}</ResponseTime>\n`;
        xmlData += `  </Week>\n`;
      });
    } else {
      xmlData += `  <Message>No data available for the selected period.</Message>\n`;
    }
    xmlData += `</EmergencyResponseTimes>`;

    const blob = new Blob([xmlData], { type: "application/xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `emergency_response_time_${year}_${month}.xml`;
    link.click();
  };

  const downloadGraph = async () => {
  try {
    const dataURI = await ApexCharts.exec("emergency-response-bar", "dataURI", {
      scale: 2,
      width: 1200
    });
    
    // Create a canvas to add heading to the chart image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height + 50; // Extra space for heading
      
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add heading
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Average Emergency Response Time', canvas.width / 2, 30);
      
      // Add period information
      const monthName = months[month - 1];
      ctx.font = '16px Arial';
      ctx.fillText(`Period: ${monthName} ${year}`, canvas.width / 2, 55);
      
      // Draw the chart image below heading
      ctx.drawImage(img, 0, 60, img.width, img.height);
      
      // Download the combined image
      const link = document.createElement('a');
      link.download = `emergency_response_time_${year}_${month}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    
    img.src = dataURI.imgURI;
  } catch (error) {
    console.error("Error downloading graph:", error);
  }
};

  useEffect(() => {
    setExportFunctions({ exportPDF, exportXML, downloadGraph });
  }, [setExportFunctions, chartData, chartInstance]);

  useEffect(() => {
    const chartContainer = document.querySelector("#emergency-response-bar");

    if (chartContainer) {
      const maxDataValue = Math.max(...chartData.data, 10);
      const maxYAxisValue = Math.ceil(maxDataValue / 5) * 5;

      const options = {
        series: [{ 
          name: "Response Time", 
          data: chartData.data 
        }],
        chart: {
          id: "emergency-response-bar",
          height: 265,
          type: "bar",
          fontFamily: "Plus Jakarta Sans, sans-serif",
          foreColor: "#adb0bb",
          toolbar: { 
            show: false,
            export: {
              png: {
                filename: `emergency_response_time_${year}_${month}`,
              }
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
          events: {
            mounted: function(chartContext, config) {
              setChartInstance(chartContext);
            },
            updated: function(chartContext, config) {
              setChartInstance(chartContext);
            }
          }
        },
        colors: ["#007bff"] ,
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "40%",
            borderRadiusApplication: "end",
            dataLabels: {
              position: 'top'
            }
          },
        },
        stroke: { 
          show: false,
          width: 2,
          colors: ['transparent']
        },
        dataLabels: { 
          enabled: true,
          formatter: function(val) {
            return val ? val.toFixed(1) : '0.0';
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ["#304758"]
          }
        },
        legend: { show: false },
        grid: { 
          show: false,
          borderColor: '#e0e0e0',
          strokeDashArray: 3
        },
        yaxis: {
          min: 0,
          max: maxYAxisValue,
          tickAmount: 4,
          labels: {
            formatter: (val) => val.toFixed(0),
            style: {
              colors: '#6b7280',
              fontSize: '12px'
            }
          },
          title: {
            text: 'Response Time (minutes)',
            style: {
              color: '#6b7280',
              fontSize: '12px'
            }
          }
        },
        xaxis: {
          categories: chartData.labels,
          axisTicks: { show: false },
          labels: {
            style: {
              colors: '#6b7280',
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          theme: "dark",
          x: { show: true },
          y: {
            formatter: function(val) {
              return val ? `${val.toFixed(2)} minutes` : '0.00 minutes';
            }
          },
          style: {
            fontSize: '12px'
          }
        },
        states: {
          hover: {
            filter: {
              type: 'darken',
              value: 0.8,
            }
          }
        }
      };

      const chart = new ApexCharts(chartContainer, options);
      chart.render();
      setChartInstance(chart);

      return () => {
        chart.destroy();
        setChartInstance(null);
      };
    }
  }, [chartData, month, year]);

  return (
    <>
      <div id="emergency-response-bar"></div>
      {noData && (
        <div className="text-center mt-3 fw-semibold text-muted">
          Data is Not Available
        </div>
      )}
    </>
  );
};

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const EmergencyResponseCard = () => {
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const [exportFunctions, setExportFunctions] = useState({
    exportPDF: () => {},
    exportXML: () => {},
    downloadGraph: () => {},
  });

  return (
    <div className="card analytic-card w-100 position-relative overflow-hidden mb-0 h-100">
      <div className="card-body">
        <div className="top-wrap mb-4">
          <h5 className="card-title mb-0 fw-semibold w-100">
            Average Emergency Response Time
          </h5>
          
          <div className="select-panel d-flex gap-2 w-100 mt-3">
            <select
              className="form-select text-dark"
              value={filters.month}
              onChange={(e) =>
                setFilters({ ...filters, month: Number(e.target.value) })
              }
            >
              {months.map((m, i) => (
                <option key={i + 1} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className="form-select text-dark"
              value={filters.year}
              onChange={(e) =>
                setFilters({ ...filters, year: Number(e.target.value) })
              }
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <div className="dropdown">
              <button
                className="btn btn-outline-primary dropdown-toggle"
                type="button"
                id="exportDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Options
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="exportDropdown">
                <li>
                  <button className="dropdown-item" onClick={exportFunctions.exportPDF}>
                    Export PDF
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={exportFunctions.exportXML}>
                    Export XML
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={exportFunctions.downloadGraph}>
                    Download Graph
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <EmergencyResponseBarChart
            month={filters.month}
            year={filters.year}
            setExportFunctions={setExportFunctions}
            filters={filters} // Add this line to pass the filters object
          />

        <div className="d-flex align-items-center justify-content-center mt-3">
          <span className="round-8 text-bg-primary rounded-circle me-2 d-inline-block"></span>
          <span>Response Time (minutes)</span>
        </div>
      </div>
    </div>
  );
};

export default EmergencyResponseCard;